import React from 'react';

import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { signUp, updateNewsletterPopUpData } from '../../actions/newsletterAction';
import { setNewsletterBannerSessionDismissed } from '../../helpers/cookie';

import { NewsletterBannerContainer, NewsletterBannerWrapper } from './NewsletterBanner.style';

import { useAuth0 } from '@auth0/auth0-react';
import { __ } from '../../helpers/TranslationService';
import { featureFlagEnabled } from '../../helpers/featureFlagEnabled';
import { MarketContext } from '../../context/markets';

import { useReducer } from 'react';
import { newsletterReducer, NewsletterStepsEnum, initialState } from './reducer';
import { NewsletterInitialScreen } from './NewsletterInitialScreen';
import { NewsletterSubscribeScreen } from './NewsletterSubscribeScreen';
import { NewsletterSuccessScreen } from './NewsletterSuccessScreen';
import { IPageDataReducer } from '../../reducers/pageData';
import { eventTracker } from '../../helpers/EventTracker/EventTracker';

export const NewsletterBanner = ({ pageData }: { pageData?: IPageDataReducer }) => {
    const [activeSSOMethod, setActiveSSOMethod] = React.useState<string | null>(null);

    const {
        markets: { isDreamlinesDE, isCruise1stUK },
    } = React.useContext(MarketContext);

    const auth0 = useAuth0();
    const dispatch = useDispatch();

    const newsletterPage = useSelector((state: RootStateOrAny) => state.newsletterPage);
    const appConfig = pageData?.appConfig;
    const { popUp } = newsletterPage;
    const { shouldShowNewsletterBanner } = popUp;

    const [newsletterState, dispatchNewsletterAction] = useReducer(newsletterReducer, initialState);

    React.useEffect(() => {
        if (auth0.isLoading || !auth0.isAuthenticated) return;
        if (auth0.isAuthenticated && !activeSSOMethod) {
            auth0.logout({ logoutParams: { returnTo: window.location.origin }, openUrl: false });
        }

        if (shouldShowNewsletterBanner && auth0.isAuthenticated && auth0.user?.email) {
            handleEmailSubscription(auth0.user.email, activeSSOMethod);
        }
    }, [auth0, activeSSOMethod, shouldShowNewsletterBanner]);

    const isGoogleSSOEnabled = featureFlagEnabled(pageData?.featureFlags, 'newsletterGoogleSSO');
    const isAppleSSOEnabled = featureFlagEnabled(pageData?.featureFlags, 'newsletterAppleSSO');
    const isNetIDSSOEnabled = featureFlagEnabled(pageData?.featureFlags, 'newsletterNetIDSSO');
    const isHotmailSSOEnabled = featureFlagEnabled(pageData?.featureFlags, 'newsletterHotmailSSO');

    const handleGoogleSSO = async () => {
        setActiveSSOMethod('google');

        try {
            await auth0.loginWithPopup({
                authorizationParams: {
                    connection: 'google-oauth2',
                    redirect_uri: window.location.origin,
                    scope: 'openid email',
                    prompt: 'select_account',
                },
            });
        } catch (error) {
            console.error('Google SSO failed:', error);
            setActiveSSOMethod(null);
        }
    };

    const handleHotmailSSO = async () => {
        setActiveSSOMethod('hotmail');

        try {
            await auth0.loginWithPopup({
                authorizationParams: {
                    connection: 'windowslive',
                    redirect_uri: window.location.origin,
                    scope: 'openid email',
                    prompt: 'select_account',
                },
            });
        } catch (error) {
            console.error('Hotmail SSO failed:', error);
            setActiveSSOMethod(null);
        }
    };

    const handleAppleSSO = async () => {
        setActiveSSOMethod('google');

        try {
            await auth0.loginWithPopup({
                authorizationParams: {
                    connection: 'apple',
                    redirect_uri: window.location.origin,
                    scope: 'openid email',
                    prompt: 'select_account',
                },
            });
        } catch (error) {
            setActiveSSOMethod(null);
        }
    };

    const handleNetIDSSO = async () => {
        setActiveSSOMethod('gmx');

        try {
            await auth0.loginWithPopup({
                authorizationParams: {
                    connection: 'netid',
                    redirect_uri: window.location.origin,
                    scope: 'openid email',
                    prompt: 'select_account',
                },
            });
        } catch (error) {
            setActiveSSOMethod(null);
        }
    };

    const handleEmailSubscription = async (email: string, method: string) => {
        const exiprationDays = 365;
        dispatchNewsletterAction({
            type: 'SET_LOADING',
            payload: true,
        });

        try {
            await dispatch(
                signUp(
                    email,
                    `${pageData.brandConfig.portalId} ssoPopup ${method}`,
                    exiprationDays,
                ),
            );

            dispatchNewsletterAction({
                type: 'SET_STEP',
                payload: NewsletterStepsEnum.Success,
            });

            eventTracker.trackG4Event('newsletter', {
                step: `success ${method}`,
            });
        } catch (error) {
            // omit for now
        } finally {
            dispatchNewsletterAction({
                type: 'SET_LOADING',
                payload: false,
            });
            setActiveSSOMethod(null);
        }
    };

    const onBannerCloseHandler = () => {
        setNewsletterBannerSessionDismissed();
        dispatch(
            updateNewsletterPopUpData({
                popUp: {
                    ...popUp,
                    shouldShowNewsletterBanner: false,
                    timeout: 0,
                },
            }),
        );
    };

    const onBannerSuccessCloseHandler = () => {
        dispatch(
            updateNewsletterPopUpData({
                popUp: {
                    ...popUp,
                    shouldShowNewsletterBanner: false,
                    timeout: 0,
                },
            }),
        );
    };

    const onSignUpHandler = () => {
        dispatchNewsletterAction({
            type: 'SET_STEP',
            payload: NewsletterStepsEnum.EmailSubscription,
        });
    };

    if (!shouldShowNewsletterBanner) return null;

    return (
        <NewsletterBannerWrapper>
            <NewsletterBannerContainer>
                {newsletterState.step === NewsletterStepsEnum.Initial && (
                    <NewsletterInitialScreen
                        isGoogleSSOEnabled={isGoogleSSOEnabled}
                        isAppleSSOEnabled={isAppleSSOEnabled}
                        isNetIDSSOEnabled={isNetIDSSOEnabled}
                        isHotmailSSOEnabled={isHotmailSSOEnabled}
                        onBannerCloseHandler={onBannerCloseHandler}
                        onSignUpHandler={onSignUpHandler}
                        currency={appConfig?.defaultCurrency}
                        obcCredit={newsletterState.obcCredit[appConfig?.countryCode]}
                    />
                )}

                {newsletterState.step === NewsletterStepsEnum.EmailSubscription && (
                    <NewsletterSubscribeScreen
                        isLoading={newsletterState.isLoading}
                        isGoogleSSOEnabled={isGoogleSSOEnabled}
                        isAppleSSOEnabled={isAppleSSOEnabled}
                        isNetIDSSOEnabled={isNetIDSSOEnabled}
                        isHotmailSSOEnabled={isHotmailSSOEnabled}
                        onBannerCloseHandler={onBannerCloseHandler}
                        handleGoogleSSO={handleGoogleSSO}
                        handleHotmailSSO={handleHotmailSSO}
                        handleAppleSSO={handleAppleSSO}
                        handleNetIDSSO={handleNetIDSSO}
                        handleEmailSubscription={handleEmailSubscription}
                        onStepBack={() =>
                            dispatchNewsletterAction({
                                type: 'SET_STEP',
                                payload: NewsletterStepsEnum.Initial,
                            })
                        }
                    />
                )}

                {newsletterState.step === NewsletterStepsEnum.Success && (
                    <NewsletterSuccessScreen
                        enableCatalogueSignUp={isCruise1stUK || isDreamlinesDE}
                        onBannerCloseHandler={onBannerSuccessCloseHandler}
                    />
                )}
            </NewsletterBannerContainer>
        </NewsletterBannerWrapper>
    );
};
